import React, { useEffect, useState } from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import { LuImage, LuLibrary, LuVideo, LuArrowLeft } from "react-icons/lu";
import AppModal from "../../common/modal/AppModal";
import LibraryImageTab from "../../add_image_modal/LibraryImageTab";
import "../../add_image_modal/_addImageModal.scss";

const thumb = {
  width: "100%",
  height: "110px",
  objectFit: "contain",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const tile = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px",
  marginTop: "8px",
  marginBottom: 0,
  borderRadius: "10px",
  border: "2px dashed #e2e8f0",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1.4,
  textAlign: "left",
  cursor: "pointer",
  width: "100%",
};

// The studio's dashed "upload or drag & drop" tile + "Choose from library" tile (same look as the
// Replace-product reference picker in ImageAnimatorModal), compact and stacked in one column.
function MediaTiles({ video, uploading, onFile, onLibrary }) {
  const [drag, setDrag] = useState(false);
  const Icon = video ? LuVideo : LuImage;
  return (
    <>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (!uploading) onFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
        }}
        style={{
          ...tile,
          borderColor: drag ? "#a5b4fc" : "#e2e8f0",
          background: drag ? "#eef2ff" : "#f8fafc",
          color: drag ? "#4f46e5" : "#64748b",
          cursor: uploading ? "wait" : "pointer",
        }}
      >
        <Icon size={18} style={{ color: "#7c3aed", flex: "none" }} />
        {uploading
          ? i18n.t("Uploading")
          : video
            ? i18n.t("marks.upload_video_hint")
            : i18n.t("ReplaceProduct.UploadHint")}
        <input
          type="file"
          accept={video ? "video/mp4,video/webm" : "image/*"}
          style={{ display: "none" }}
          disabled={uploading}
          onChange={(e) => {
            onFile(e.target.files && e.target.files[0]);
            e.target.value = "";
          }}
        />
      </label>
      <button type="button" onClick={onLibrary} disabled={uploading} style={tile}>
        <LuLibrary size={18} style={{ color: "#7c3aed", flex: "none" }} />
        {i18n.t("ReplaceProduct.PickFromLibrary")}
      </button>
    </>
  );
}

MediaTiles.propTypes = {
  video: PropTypes.bool,
  uploading: PropTypes.bool,
  onFile: PropTypes.func.isRequired,
  onLibrary: PropTypes.func.isRequired,
};

function Preview({ url, video }) {
  if (!url) {
    return (
      <div
        style={{
          ...thumb,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
          fontSize: "12px",
        }}
      >
        {i18n.t("marks.img_after_empty")}
      </div>
    );
  }
  return video ? (
    <video src={url} style={thumb} muted preload="metadata" />
  ) : (
    <img src={url} alt="" style={thumb} />
  );
}

Preview.propTypes = { url: PropTypes.string, video: PropTypes.bool };

// Click action for a plain image box: show another image (or play a video) once it is clicked,
// optionally holding the timeline until then. Stored in the box plugin state as
// `clickAction = { afterType: 'image'|'video', afterImage, afterVideo, pauseUntilClicked }`
// (old `{ afterImage, pauseUntilClicked }` reads as image). The library browser replaces this
// modal's content in place (never a second popup). Changing "Before" updates the box's `state.url`.
export default function ImageClickActionModal({
  visible,
  onClose,
  beforeUrl,
  clickAction,
  onSave,
  onRemove,
  uploadFunction,
}) {
  const [before, setBefore] = useState("");
  const [afterType, setAfterType] = useState("image");
  const [afterImage, setAfterImage] = useState("");
  const [afterVideo, setAfterVideo] = useState("");
  const [pause, setPause] = useState(false);
  const [picking, setPicking] = useState(null); // "before" | "after" while the library is shown
  const [uploading, setUploading] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!visible) return;
    setBefore(beforeUrl || "");
    setAfterType((clickAction && clickAction.afterType) === "video" ? "video" : "image");
    setAfterImage((clickAction && clickAction.afterImage) || "");
    setAfterVideo((clickAction && clickAction.afterVideo) || "");
    setPause(!!(clickAction && clickAction.pauseUntilClicked));
    setPicking(null);
    setUploading(null);
    setError("");
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const isVideo = afterType === "video";
  const afterUrl = isVideo ? afterVideo : afterImage;

  const apply = (slot, url) => {
    if (slot === "before") setBefore(url);
    else if (isVideo) setAfterVideo(url);
    else setAfterImage(url);
  };

  const upload = (slot, file) => {
    if (!file || !uploadFunction) return;
    const wantVideo = slot === "after" && isVideo;
    if (!file.type.startsWith(wantVideo ? "video/" : "image/")) {
      setError(i18n.t(wantVideo ? "marks.err_video_file" : "ReplaceProduct.InvalidFileType"));
      return;
    }
    setError("");
    setUploading(slot);
    uploadFunction(file, "", (url) => {
      setUploading(null);
      if (url) apply(slot, url);
    });
  };

  const footer = (
    <div className="app-modal__footer">
      {clickAction && (
        <button
          type="button"
          className="app-modal__btn app-modal__btn--danger"
          onClick={onRemove}
          style={{ marginRight: "auto" }}
        >
          {i18n.t("marks.img_remove")}
        </button>
      )}
      <button type="button" className="app-modal__btn app-modal__btn--ghost" onClick={onClose}>
        {i18n.t("Cancel")}
      </button>
      <button
        type="button"
        className="app-modal__btn app-modal__btn--primary"
        disabled={!!uploading || !!picking || (!afterUrl && !pause)}
        onClick={() =>
          onSave(
            { afterType, afterImage, afterVideo, pauseUntilClicked: pause },
            before !== beforeUrl ? before : undefined
          )
        }
      >
        {i18n.t("marks.save_changes")}
      </button>
    </div>
  );

  const segment = (active) => ({
    flex: 1,
    padding: "4px 8px",
    fontSize: "12px",
    fontWeight: 600,
    borderRadius: "8px",
    border: `1px solid ${active ? "#a5b4fc" : "#e2e8f0"}`,
    background: active ? "#eef2ff" : "#fff",
    color: active ? "#4f46e5" : "#64748b",
    cursor: "pointer",
  });

  return (
    <AppModal
      show={!!visible}
      onClose={onClose}
      title={i18n.t("marks.img_click_action")}
      size="md"
      footer={footer}
    >
      {picking ? (
        <div>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            style={{ fontSize: 12, padding: "4px 10px", marginBottom: 10 }}
            onClick={() => setPicking(null)}
          >
            <LuArrowLeft size={14} /> {i18n.t("Back")}
          </button>
          <LibraryImageTab
            fileType={picking === "after" && isVideo ? "video" : "image"}
            onAddToScene={(url) => {
              apply(picking, url);
              setPicking(null);
            }}
          />
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="app-modal__toggle-title">{i18n.t("marks.img_before")}</div>
              <Preview url={before} />
              <MediaTiles
                uploading={uploading === "before"}
                onFile={(f) => upload("before", f)}
                onLibrary={() => setPicking("before")}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="app-modal__toggle-title">{i18n.t("marks.img_after")}</div>
              <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                <button
                  type="button"
                  style={segment(!isVideo)}
                  onClick={() => setAfterType("image")}
                >
                  {i18n.t("marks.type_image")}
                </button>
                <button
                  type="button"
                  style={segment(isVideo)}
                  onClick={() => setAfterType("video")}
                >
                  {i18n.t("marks.type_video")}
                </button>
              </div>
              <Preview url={afterUrl} video={isVideo} />
              <MediaTiles
                video={isVideo}
                uploading={uploading === "after"}
                onFile={(f) => upload("after", f)}
                onLibrary={() => setPicking("after")}
              />
            </div>
          </div>
          {error && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#dc2626" }}>{error}</div>
          )}
          <div
            className={`app-modal__toggle-card${pause ? " app-modal__toggle-card--active" : ""}`}
            style={{ marginTop: "14px" }}
          >
            <div className="app-modal__toggle-info">
              <span className="app-modal__toggle-title">{i18n.t("marks.pause_until_clicked")}</span>
              <span className="app-modal__toggle-subtitle">
                {i18n.t(isVideo ? "marks.pause_hint_video" : "marks.pause_hint")}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pause}
              className={`app-modal__toggle-switch${pause ? " app-modal__toggle-switch--on" : ""}`}
              onClick={() => setPause(!pause)}
            >
              <span />
            </button>
          </div>
        </>
      )}
    </AppModal>
  );
}

ImageClickActionModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  beforeUrl: PropTypes.string,
  clickAction: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  uploadFunction: PropTypes.func,
};
